import { getCookie } from '../utils/utils';
import { getBuildingListings } from '../utils/buildingListings';
import { performReverseGeocodingAndFetchPOIs } from '../HouseMap/BuildingMap.helpers';
import type { BuildingWithConvertedAddresses } from '../utils/types';

export type BuildingSeed = { id: string; lat: number; lng: number };

const finite = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n);

// Fetch with an abort timeout so a stalled backend can't hang a cold deep-link
// (this runs on initial page load and blocks the building view until it settles).
const fetchWithTimeout = (
  url: string,
  options: RequestInit,
  timeoutMs = 8000,
): Promise<Response> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timeout),
  );
};

/**
 * Resolve a coordinate seed for a cold building deep-link, in priority order:
 *  1. lat/lng already in the URL (written by every in-app building link)
 *  2. a listing's address coordinates (the building is active — backend listings)
 *  3. the id->centroid index (a building seen on the map before)
 * Returns null when the building cannot be located (never-seen, listing-less) —
 * the caller then degrades to the normal location flow.
 *
 * A Mapbox feature id cannot be resolved to a location by Mapbox, so this seed
 * is what lets the cold link center the map and resolve the footprint by id.
 */
export async function resolveBuildingSeed(
  buildingId: string,
  urlLat: string | null,
  urlLng: string | null,
): Promise<BuildingSeed | null> {
  const lat = urlLat != null ? Number(urlLat) : NaN;
  const lng = urlLng != null ? Number(urlLng) : NaN;
  if (finite(lat) && finite(lng)) return { id: buildingId, lat, lng };

  const headers = {
    'Content-Type': 'application/json',
    'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
  };

  // 2. A listing's canonical coordinates. The same timeout applies: this runs
  // on a cold deep-link, so the shared client is given an abort signal rather
  // than being allowed to hang the building view behind a stalled backend.
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    const rows = await getBuildingListings(buildingId, {
      signal: controller.signal,
    }).finally(() => clearTimeout(timeout));
    const listing = rows?.find((c) => c.address?.coordinates);
    const co = listing?.address?.coordinates;
    if (co && finite(co.lat) && finite(co.lng)) {
      return { id: buildingId, lat: co.lat, lng: co.lng };
    }
  } catch {
    /* fall through */
  }

  // 3. The id->centroid index (building seen on the map before).
  try {
    const res = await fetchWithTimeout(
      `${process.env.REACT_APP_API_URL}/building-centroid?id=${buildingId}`,
      { credentials: 'include', headers },
    );
    const data = await res.json();
    const c = data?.centroid;
    if (c && finite(c.lat) && finite(c.lng)) {
      return { id: buildingId, lat: c.lat, lng: c.lng };
    }
  } catch {
    /* fall through */
  }

  return null;
}

/**
 * Geocodes a cold deep link's seed point at once, as master does, and hands the
 * enriched building to `onEnriched`: the geocoder's first address or, with
 * none, the district-only name a map click gets (JAA-417). It works on a copy,
 * so the seed held in React state is never mutated. A listing's own address
 * still wins in BuildingView.
 */
export function geocodeColdSeed(
  seed: BuildingWithConvertedAddresses,
  onEnriched: (building: BuildingWithConvertedAddresses) => void,
): void {
  performReverseGeocodingAndFetchPOIs(
    {
      ...seed,
      addresses: [],
      properties: { ...seed.properties, addresses: [] },
      pois: [],
    },
    onEnriched,
    seed.centroid,
    false,
    true,
  );
}
